import { REPORT_PARAMS } from '@/api/reports';
import { chart } from '@/theme';
import {
  leadStatus,
  leadStatusOrder,
  orderStatus,
  orderStatusFlow,
  type ToneName,
} from '@/theme/status';
import {
  ZONA_HORARIA_POR_DEFECTO,
  diasEntre,
  formatearEnZona,
  hoyEnZona,
  instanteEnZona,
  primerDiaDelMes,
  sumarDias,
} from '@/lib/timezone';
import type { Channel, EstadoLead, EstadoOrden } from '@/types/domain';
import type {
  AgruparCampanas,
  CeldaHorario,
  FilaCampanaReporte,
  IntervaloReporte,
  MotivoNoCierre,
  OrdenProductos,
  PeriodoReporte,
  ProductoVendido,
  PuntoSerie,
  ReportFiltros,
  ReporteProductos,
  ReporteResumen,
  ReporteSerie,
  ResumenConversaciones,
  TotalesCampanas,
} from '@/types/reports';

/**
 * Formato y estado de URL de los reportes (módulo 10).
 *
 * Todo lo de acá es PURO (entra data, sale texto/estructura): así la pantalla no repite cálculos y
 * las comprobaciones se pueden hacer sin montar React. Tres reglas del módulo:
 *
 *  1. `null` NO es cero. El backend devuelve `cpa`, `ticketPromedio` y las tasas en `null` cuando no
 *     se pueden calcular (una fila con gasto y sin ventas, o un periodo sin conversaciones). Acá se
 *     pintan «—» y, cuando hace falta, se explica por qué: un «0» sería una mentira.
 *  2. Las fechas se cuentan en la zona del NEGOCIO (ver `@/lib/timezone`), nunca en la del navegador:
 *     la etiqueta de un día y el filtro que se manda tienen que hablar del mismo día.
 *  3. El dinero va como NÚMERO en formato es-CO y sin símbolo, igual que en Órdenes y Campañas: el
 *     backend no manda la moneda en estos endpoints, así que no se inventa un «$» (puede ser COP,
 *     MXN o USD). La pantalla dice de qué es cada columna.
 */

const LOCALE = 'es-CO';

/** Filtros vacíos (sin fecha, sin canal, sin anuncio). */
export const EMPTY_REPORT_FILTROS: ReportFiltros = {
  desde: '',
  hasta: '',
  channel: '',
  adId: '',
  campaignId: '',
};

/** "25.000" / "22.500,5" (es-CO). Mismo formato que Órdenes y Campañas. */
export function formatNumber(value: number): string {
  return value.toLocaleString(LOCALE, { maximumFractionDigits: 2 });
}

/** Números contables sin decimales: conversaciones, mensajes, órdenes. */
export function formatCount(value: number): string {
  return value.toLocaleString(LOCALE, { maximumFractionDigits: 0 });
}

/** «3 conversaciones» / «1 conversación» (plural explícito: en es no se pluraliza solo). */
export function countLabel(count: number, singular: string, plural: string): string {
  return `${formatCount(count)} ${count === 1 ? singular : plural}`;
}

/**
 * «12,5 %» — recibe el porcentaje YA multiplicado por 100 (como lo manda el backend:
 * `conversionPorcentaje`, `porcentaje`, `tasaConversionPorcentaje`). `null` → «—».
 */
export function formatPorcentaje(valor: number | null | undefined, decimales = 1): string {
  if (valor === null || valor === undefined) return '—';

  return `${valor.toLocaleString(LOCALE, {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  })} %`;
}

/** «1,8»: promedios (ticket, mensajes por conversación) con un decimal. `null` → «—». */
export function formatPromedio(valor: number | null | undefined, decimales = 1): string {
  if (valor === null || valor === undefined) return '—';

  return valor.toLocaleString(LOCALE, {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
}

/** Nombre visible de cada canal (el enum del backend es `Channel`). */
export const CHANNEL_LABEL: Record<string, string> = {
  WHATSAPP: 'WhatsApp',
  INSTAGRAM: 'Instagram',
  MESSENGER: 'Messenger',
  TIKTOK: 'TikTok',
};

/** Opciones del filtro de canal: «Todos los canales» + los cuatro del dominio. */
export const CHANNEL_FILTROS: { value: string; label: string }[] = [
  { value: '', label: 'Todos los canales' },
  ...Object.entries(CHANNEL_LABEL).map(([value, label]) => ({ value, label })),
];

/** Filtros de la URL (`?desde=…&channel=…`) a `ReportFiltros`. Ignora cualquier otro parámetro. */
export function reportFiltrosFrom(params: URLSearchParams): ReportFiltros {
  return {
    desde: params.get(REPORT_PARAMS.desde) ?? '',
    hasta: params.get(REPORT_PARAMS.hasta) ?? '',
    channel: params.get(REPORT_PARAMS.channel) ?? '',
    adId: params.get(REPORT_PARAMS.adId) ?? '',
    campaignId: params.get(REPORT_PARAMS.campaignId) ?? '',
  };
}

/**
 * Escribe un parámetro de la URL, o lo borra con `null`/`''`. Devuelve un `URLSearchParams` NUEVO
 * (no muta el que recibe) porque la pantalla lo entrega tal cual a `setSearchParams`.
 */
export function conParametro(
  actuales: URLSearchParams,
  key: string,
  value: string | null,
): URLSearchParams {
  const siguiente = new URLSearchParams(actuales);

  if (value === null || value === '') siguiente.delete(key);
  else siguiente.set(key, value);

  return siguiente;
}

/**
 * Aplica cambios parciales de filtros sobre la URL conservando el resto de parámetros (el
 * `intervalo` del gráfico, la vista de una tabla…): los filtros vacíos se BORRAN, así un enlace
 * compartido no arrastra `?channel=`.
 */
export function applyReportFiltros(
  actuales: URLSearchParams,
  cambios: Partial<ReportFiltros>,
): URLSearchParams {
  let siguiente = new URLSearchParams(actuales);

  for (const [key, value] of Object.entries(cambios)) {
    if (!(key in EMPTY_REPORT_FILTROS)) continue;
    siguiente = conParametro(siguiente, REPORT_PARAMS[key as keyof ReportFiltros], value ?? '');
  }

  return siguiente;
}

/** ¿Hay algún filtro puesto? (para mostrar «Quitar los filtros» y el resumen en prosa). */
export function hayFiltros(filtros: ReportFiltros): boolean {
  return Object.values(filtros).some((valor) => valor !== '');
}

const INTERVALOS: IntervaloReporte[] = ['dia', 'semana', 'mes'];
const AGRUPAR: AgruparCampanas[] = ['anuncio', 'campania'];
const ORDENAR: OrdenProductos[] = ['cantidad', 'ingresos'];

/** Una opción de un `enum` de la query, con default si el parámetro falta o no es válido. */
function opcionDeLista<T extends string>(
  params: URLSearchParams,
  key: string,
  opciones: T[],
  porDefecto: T,
): T {
  const valor = params.get(key);
  return opciones.includes(valor as T) ? (valor as T) : porDefecto;
}

/** `intervalo` de la URL (`dia` por defecto: es lo que el backend usa si no se manda). */
export function intervaloFrom(params: URLSearchParams): IntervaloReporte {
  return opcionDeLista(params, REPORT_PARAMS.intervalo, INTERVALOS, 'dia');
}

/** `agrupar` de la URL (`anuncio` por defecto, igual que el backend). */
export function agruparFrom(params: URLSearchParams): AgruparCampanas {
  return opcionDeLista(params, REPORT_PARAMS.agrupar, AGRUPAR, 'anuncio');
}

/** `ordenar` de la URL (`cantidad` por defecto). */
export function ordenarFrom(params: URLSearchParams): OrdenProductos {
  return opcionDeLista(params, REPORT_PARAMS.ordenar, ORDENAR, 'cantidad');
}

/** Opciones de `limite` del ranking de productos (el backend acepta 1..100). */
export const LIMITES_PRODUCTOS = [5, 10, 20, 50] as const;

/** `limite` de la URL, acotado a las opciones de la pantalla (`10` por defecto). */
export function limiteProductosFrom(params: URLSearchParams): number {
  const valor = Number(params.get(REPORT_PARAMS.limite));
  return (LIMITES_PRODUCTOS as readonly number[]).includes(valor) ? valor : 10;
}

/** Atajos de rango. `dias: null` = «este mes» (se calcula desde el día 1). */
export interface RangoRapidoDef {
  id: RangoRapido;
  label: string;
  dias: number | null;
}

/** Rangos de un clic, los mismos tres de siempre + el mes en curso. */
export type RangoRapido = '7d' | '30d' | '90d' | 'mes';

export const RANGOS_RAPIDOS: RangoRapidoDef[] = [
  { id: '7d', label: '7 días', dias: 7 },
  { id: '30d', label: '30 días', dias: 30 },
  { id: '90d', label: '90 días', dias: 90 },
  { id: 'mes', label: 'Este mes', dias: null },
];

/**
 * Fechas de un rango rápido, en DÍAS del negocio.
 *
 * `hasta` es hoy y el rango es inclusivo (7 días = hoy + los 6 anteriores): es como cuenta la gente
 * («los últimos 7 días incluyen hoy») y coincide con el default del backend, que también compara
 * contra `new Date()`.
 */
export function rangoRapido(
  id: RangoRapido,
  zonaHoraria: string,
  ahora: Date = new Date(),
): Pick<ReportFiltros, 'desde' | 'hasta'> {
  const hoy = hoyEnZona(zonaHoraria, ahora);

  if (id === 'mes') return { desde: primerDiaDelMes(hoy), hasta: hoy };

  const dias = RANGOS_RAPIDOS.find((rango) => rango.id === id)?.dias ?? 30;
  return { desde: sumarDias(hoy, -(dias - 1)), hasta: hoy };
}

/**
 * Qué botón de rango rápido está «encendido» según los filtros puestos (`null` = ninguno: los filtros
 * son un rango a mano, o no hay fechas y manda el default del backend).
 *
 * Se compara contra las fechas calculadas, así el botón queda marcado también cuando alguien abre un
 * enlace compartido con `?desde=…&hasta=…` escrito a mano.
 */
export function rangoActivo(
  filtros: Partial<ReportFiltros>,
  zonaHoraria: string,
  ahora: Date = new Date(),
): RangoRapido | null {
  const encontrado = RANGOS_RAPIDOS.find((rango) => {
    const fechas = rangoRapido(rango.id, zonaHoraria, ahora);
    return filtros.desde === fechas.desde && filtros.hasta === fechas.hasta;
  });

  return encontrado?.id ?? null;
}

/** ¿El rango quedó invertido (`desde` posterior a `hasta`)? La pantalla lo avisa antes de consultar. */
export function rangoInvertido(filtros: Partial<ReportFiltros>): boolean {
  if (!filtros.desde || !filtros.hasta) return false;

  const dias = diasEntre(filtros.desde, filtros.hasta);
  return dias !== null && dias < 0;
}

/** «10 sept 2026» de un día del negocio (`YYYY-MM-DD`), leído en la zona del negocio. */
export function etiquetaDia(fecha: string, zonaHoraria: string): string {
  const instante = instanteEnZona(fecha, zonaHoraria);
  return instante ? formatearEnZona(instante, zonaHoraria) : fecha;
}

/** «17 ago 2026» de un instante ISO que devolvió el backend, EN la zona del negocio. */
export function etiquetaInstante(instante: string, zonaHoraria: string): string {
  return formatearEnZona(instante, zonaHoraria);
}

/**
 * Los filtros puestos, en prosa: «del 10 sept 2026 al 17 sept 2026, canal WhatsApp, anuncio 123».
 * Sin filtros devuelve la frase que explica que manda el default del backend.
 */
export function describirFiltros(filtros: ReportFiltros, zonaHoraria: string): string {
  const partes: string[] = [];

  if (filtros.desde && filtros.hasta) {
    partes.push(`del ${etiquetaDia(filtros.desde, zonaHoraria)} al ${etiquetaDia(filtros.hasta, zonaHoraria)}`);
  } else if (filtros.desde) {
    partes.push(`desde el ${etiquetaDia(filtros.desde, zonaHoraria)}`);
  } else if (filtros.hasta) {
    partes.push(`hasta el ${etiquetaDia(filtros.hasta, zonaHoraria)}`);
  }

  if (filtros.channel) partes.push(`canal ${CHANNEL_LABEL[filtros.channel] ?? filtros.channel}`);
  if (filtros.adId) partes.push(`anuncio ${filtros.adId}`);
  if (filtros.campaignId) partes.push(`campaña ${filtros.campaignId}`);

  return partes.length > 0 ? partes.join(', ') : 'sin filtros';
}

/**
 * «del 17 ago 2026 al 17 sept 2026 (America/Bogota)»: el periodo REAL que usó el backend.
 *
 * Se muestra siempre, incluso sin filtros, porque el backend aplica su propio default cuando no se
 * mandan fechas y el admin tiene derecho a saber qué ventana está viendo.
 */
export function describirPeriodoEfectivo(periodo: PeriodoReporte, zonaHoraria: string): string {
  const zona = periodo.zonaHoraria ?? zonaHoraria;
  return `del ${etiquetaInstante(periodo.desde, zona)} al ${etiquetaInstante(periodo.hasta, zona)} (${zona})`;
}

// --- Embudo del periodo (bloque «Conversaciones → ventas» del panel) ---

/** Un escalón del embudo: cuántos llegaron y qué porcentaje de las conversaciones representa. */
export interface PasoEmbudo {
  id: 'conversaciones' | 'conOrden' | 'ventasConfirmadas';
  label: string;
  valor: number;
  /** % sobre las conversaciones del periodo (`null` si no hubo conversaciones). */
  porcentaje: number | null;
  detalle: string;
}

/**
 * El embudo del periodo, con los tres escalones que el backend sí puede sostener:
 * conversaciones → conversaciones con orden → ventas confirmadas.
 *
 * No se inventa un escalón de «leads contactados» ni de embudo por estados de la orden: el resumen
 * solo cuenta esas tres cosas (`docs/REPORTES.md` §3).
 */
export function embudoResumen(resumen: ReporteResumen): PasoEmbudo[] {
  const conversaciones = resumen.conversaciones.total;

  return [
    {
      id: 'conversaciones',
      label: 'Conversaciones',
      valor: conversaciones,
      porcentaje: conversaciones > 0 ? 100 : null,
      detalle: `${countLabel(resumen.conversaciones.activas, 'con actividad', 'con actividad')} en el periodo`,
    },
    {
      id: 'conOrden',
      label: 'Llegaron a una orden',
      valor: resumen.conversaciones.conOrden,
      porcentaje: resumen.tasas.cierreDeLeadsPorcentaje,
      detalle: `${countLabel(resumen.ordenes.total, 'orden creada', 'órdenes creadas')} en total`,
    },
    {
      id: 'ventasConfirmadas',
      label: 'Ventas confirmadas',
      valor: resumen.ordenes.ventasConfirmadas,
      porcentaje: resumen.tasas.conversionPorcentaje,
      detalle: `${formatCount(resumen.ordenes.pendientesConfirmacion)} por confirmar · ${formatCount(
        resumen.ordenes.canceladas,
      )} canceladas`,
    },
  ];
}

// --- Serie temporal (el gráfico del panel y el bloque «Evolución» de reportes) ---

/** Métrica graficable de la serie (las cinco que devuelve `PuntoSerie`). */
export type MetricaSerie =
  | 'conversaciones'
  | 'mensajes'
  | 'ordenes'
  | 'ventasConfirmadas'
  | 'ingresos';

export interface MetricaSerieDef {
  id: MetricaSerie;
  label: string;
  /** Etiqueta corta, para los botones del selector. */
  corto: string;
  /** Color de la serie (`chart.series` de `@/theme`, el mismo orden que la referencia visual). */
  color: string;
  /** Cómo se pinta el valor (y su total en el encabezado). */
  formatear: (valor: number) => string;
}

export const METRICAS_SERIE: MetricaSerieDef[] = [
  { id: 'conversaciones', label: 'Conversaciones nuevas', corto: 'Conversaciones', color: chart.series[0], formatear: formatCount },
  { id: 'mensajes', label: 'Mensajes', corto: 'Mensajes', color: chart.series[1], formatear: formatCount },
  { id: 'ordenes', label: 'Órdenes creadas', corto: 'Órdenes', color: chart.series[4], formatear: formatCount },
  { id: 'ventasConfirmadas', label: 'Ventas confirmadas', corto: 'Ventas', color: chart.series[2], formatear: formatCount },
  { id: 'ingresos', label: 'Ingresos', corto: 'Ingresos', color: chart.series[5], formatear: formatNumber },
];

/** Definición de una métrica por id (nunca falla: `conversaciones` es el default). */
export function metricaSerie(id: MetricaSerie): MetricaSerieDef {
  return METRICAS_SERIE.find((metrica) => metrica.id === id) ?? METRICAS_SERIE[0];
}

/**
 * Parámetro de la URL con la métrica graficada (`?metrica=ingresos`).
 *
 * No es un parámetro del backend (la serie devuelve las cinco métricas en cada punto): vive en la URL
 * para que el gráfico elegido viaje en el enlace compartido, igual que el intervalo y los filtros.
 */
export const PARAM_METRICA = 'metrica';

/** `metrica` de la URL (`conversaciones` por defecto: la primera del panel). */
export function metricaFrom(params: URLSearchParams): MetricaSerie {
  const valor = params.get(PARAM_METRICA);
  return METRICAS_SERIE.some((metrica) => metrica.id === valor)
    ? (valor as MetricaSerie)
    : 'conversaciones';
}

/** Valor de un punto para la métrica elegida. */
export function valorSerie(punto: PuntoSerie, metrica: MetricaSerie): number {
  return punto[metrica];
}

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MESES_LARGOS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/** Partes de las tres formas de clave que manda el backend (`2026-09-10`, `2026-W38`, `2026-09`). */
function partesPunto(periodo: string): { anio: number; mes: number; dia: number; semana: number } | null {
  const dia = /^(\d{4})-(\d{2})-(\d{2})$/.exec(periodo);
  if (dia) return { anio: Number(dia[1]), mes: Number(dia[2]), dia: Number(dia[3]), semana: 0 };

  const semana = /^(\d{4})-W(\d{2})$/.exec(periodo);
  if (semana) return { anio: Number(semana[1]), mes: 0, dia: 0, semana: Number(semana[2]) };

  const mes = /^(\d{4})-(\d{2})$/.exec(periodo);
  if (mes) return { anio: Number(mes[1]), mes: Number(mes[2]), dia: 0, semana: 0 };

  return null;
}

/**
 * Etiqueta CORTA de un punto, para el eje del gráfico: «10/09» (día), «S38» (semana ISO),
 * «sep 26» (mes). En el eje no cabe nada más largo sin solaparse.
 */
export function etiquetaPunto(periodo: string, intervalo: IntervaloReporte): string {
  const partes = partesPunto(periodo);
  if (!partes) return periodo;
  if (intervalo === 'semana') return `S${partes.semana}`;
  if (intervalo === 'mes') return `${MESES_CORTOS[partes.mes - 1] ?? '?'} ${String(partes.anio).slice(2)}`;

  return `${String(partes.dia).padStart(2, '0')}/${String(partes.mes).padStart(2, '0')}`;
}

/**
 * Etiqueta LARGA de un punto, para el tooltip y las tablas: «10 sept 2026», «Semana 38 de 2026».
 * El día se lee en la zona del negocio (una clave de día es un día de ESE calendario).
 */
export function etiquetaPuntoLarga(
  periodo: string,
  intervalo: IntervaloReporte,
  zonaHoraria: string,
): string {
  const partes = partesPunto(periodo);
  if (!partes) return periodo;
  if (intervalo === 'semana') return `Semana ${partes.semana} de ${partes.anio}`;
  if (intervalo === 'mes') return `${MESES_LARGOS[partes.mes - 1] ?? '?'} ${partes.anio}`;

  return etiquetaDia(periodo, zonaHoraria);
}

/** Resumen de la serie para el encabezado del gráfico. */
export interface ResumenSerie {
  total: number;
  /** Promedio por punto (por día, por semana o por mes, según el intervalo). */
  promedio: number;
  /** El punto más alto, o `null` si la serie está toda en cero. */
  mayor: { etiqueta: string; valor: number } | null;
}

/**
 * Total, promedio y pico de la métrica elegida.
 *
 * El total de `ingresos` es la suma del periodo y los demás son conteos; en los dos casos la suma es
 * lo que el admin espera ver («cuánto entró» / «cuántas hubo»). El promedio divide por los PUNTOS
 * (no por los días con datos): la serie ya viene con los huecos en cero, así que incluir los días sin
 * actividad es lo correcto.
 */
export function resumenSerie(
  puntos: PuntoSerie[],
  metrica: MetricaSerie,
  intervalo: IntervaloReporte,
  zonaHoraria: string,
): ResumenSerie {
  const total = puntos.reduce((suma, punto) => suma + valorSerie(punto, metrica), 0);
  const mayor = puntos.reduce<PuntoSerie | null>((maximo, punto) => {
    if (!maximo) return punto;
    return valorSerie(punto, metrica) > valorSerie(maximo, metrica) ? punto : maximo;
  }, null);
  const valorMayor = mayor ? valorSerie(mayor, metrica) : 0;

  return {
    total,
    promedio: puntos.length > 0 ? total / puntos.length : 0,
    mayor:
      mayor && valorMayor > 0
        ? { etiqueta: etiquetaPuntoLarga(mayor.periodo, intervalo, zonaHoraria), valor: valorMayor }
        : null,
  };
}

// --- Publicidad (campañas/anuncios con CPA) ---

/** Clave especial del backend para las conversaciones sin `ad_id` ni `campaign_id`. */
export const CLAVE_SIN_ATRIBUCION = 'sin_atribucion';

/** ¿La fila es el cajón de «sin atribución»? (no es un anuncio, no tiene ids ni CPA propio). */
export function esSinAtribucion(fila: FilaCampanaReporte | string): boolean {
  const clave = typeof fila === 'string' ? fila : fila.clave;
  return clave === CLAVE_SIN_ATRIBUCION;
}

/**
 * Nombre de una fila del reporte de publicidad, en orden de confianza:
 * el nombre cargado en Campañas → el `ad_id`/`campaign_id` → «Sin atribución».
 *
 * El backend pone un nombre largo en la fila sin atribución («Sin atribución (orgánico o cargado a
 * mano)») y ese texto se muestra donde hay espacio: acá se acorta porque va en una barra de ranking.
 */
export function etiquetaFilaCampana(fila: FilaCampanaReporte): string {
  if (fila.nombre && fila.nombre.trim() !== '') {
    return esSinAtribucion(fila) ? 'Sin atribución' : fila.nombre;
  }

  return fila.metaAdId ?? fila.metaCampaignId ?? fila.clave;
}

/** «Anuncio 123 · Campaña 456»: los ids con los que se puede volver a filtrar. */
export function idsFilaCampana(fila: FilaCampanaReporte): string {
  const partes: string[] = [];
  if (fila.metaAdId) partes.push(`Anuncio ${fila.metaAdId}`);
  if (fila.metaCampaignId) partes.push(`Campaña ${fila.metaCampaignId}`);

  return partes.length > 0 ? partes.join(' · ') : 'Sin ids de Meta (llegó sin referral)';
}

/**
 * El CPA de una fila, listo para pintar: `null` NO es «0» — es «no se puede calcular todavía».
 *
 * Pasa en dos casos reales (`reports.service.ts`): la fila tiene gasto pero ninguna venta confirmada
 * (dividir daría infinito) o tiene ventas pero no se le cargó gasto. El texto dice cuál de los dos es.
 */
export function cpaLabel(cpa: number | null | undefined, ventasConfirmadas: number): string {
  if (cpa === null || cpa === undefined) {
    return ventasConfirmadas === 0 ? 'sin ventas en el periodo' : 'sin gasto cargado';
  }

  return formatNumber(cpa);
}

/** Una fila ya lista para `RankingBars` (el orden lo decide el backend: conversaciones). */
export interface FilaRanking {
  id: string;
  label: string;
  /** Segunda línea corta: actividad, ventas y CPA. */
  hint: string;
  valor: number;
  valorLabel: string;
}

/**
 * Las primeras `limite` filas del reporte de publicidad para el ranking del panel.
 *
 * Se descartan las filas sin actividad Y sin gasto: el backend las devuelve igual (porque están en el
 * catálogo) y en un resumen ejecutivo solo estorban. La fila de «sin atribución» NO se descarta si
 * tiene datos: es la que dice cuánto entra por fuera de la pauta.
 */
export function filasRankingCampanas(items: FilaCampanaReporte[], limite = 5): FilaRanking[] {
  return items
    .filter((fila) => fila.conversaciones > 0 || fila.gasto > 0 || fila.ingresos > 0)
    .slice(0, limite)
    .map((fila) => ({
      id: fila.clave,
      label: etiquetaFilaCampana(fila),
      hint: `${countLabel(fila.conversaciones, 'conversación', 'conversaciones')} · ${countLabel(
        fila.ordenesConfirmadas,
        'venta',
        'ventas',
      )} · CPA ${cpaLabel(fila.cpa, fila.ordenesConfirmadas)}`,
      valor: fila.conversaciones,
      valorLabel: formatCount(fila.conversaciones),
    }));
}

/** Un dato de la franja de publicidad (gasto, CPA con y sin atribuir). */
export interface DatoPublicidad {
  id: 'gastoAtribuido' | 'gastoRegistrado' | 'cpa' | 'cpaGlobal';
  label: string;
  valor: string;
  /** Qué significa el número, para que no se lea como un dato suelto. */
  hint: string;
}

/**
 * Los cuatro números de la franja de publicidad, con la diferencia entre «atribuido» y «registrado»
 * bien visible: el gasto cargado que no se pudo atribuir a ninguna conversación NO desaparece, y el
 * CPA global es el que incluye ese gasto (es el número honesto de la operación).
 */
export function datosPublicidad(totales: {
  gasto: number;
  gastoTotalRegistrado: number;
  cpa: number | null;
  cpaGlobal: number | null;
  ordenesConfirmadas: number;
}): DatoPublicidad[] {
  return [
    {
      id: 'gastoAtribuido',
      label: 'Gasto atribuido',
      valor: formatNumber(totales.gasto),
      hint: 'Gasto de las filas de este reporte',
    },
    {
      id: 'gastoRegistrado',
      label: 'Gasto registrado',
      valor: formatNumber(totales.gastoTotalRegistrado),
      hint: 'Todas las campañas activas, atribuidas o no',
    },
    {
      id: 'cpa',
      label: 'CPA atribuido',
      valor: cpaLabel(totales.cpa, totales.ordenesConfirmadas),
      hint: 'Gasto atribuido por venta confirmada',
    },
    {
      id: 'cpaGlobal',
      label: 'CPA global',
      valor: cpaLabel(totales.cpaGlobal, totales.ordenesConfirmadas),
      hint: 'Todo el gasto registrado por venta confirmada',
    },
  ];
}



// --- Horarios (heatmap día × hora) ---

/** Nombres de los días como los manda el backend: `diaSemana` 0 = domingo. */
export const DIAS_SEMANA_CORTOS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
export const DIAS_SEMANA_LARGOS = [
  'Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado',
];

/** Campo que se puede intensificar en el heatmap (los dos conteos crudos de la celda). */
export type CampoHorario = 'mensajes' | 'conversacionesIniciadas';

/** Valor de la celda según el campo elegido. */
export function valorCelda(celda: CeldaHorario, campo: CampoHorario): number {
  return campo === 'mensajes' ? celda.mensajes : celda.conversacionesIniciadas;
}

/**
 * Parámetro de la URL con el conteo que se intensifica en el mapa de calor
 * (`?horario=conversacionesIniciadas`). No es del backend: la celda trae los dos números.
 */
export const PARAM_HORARIO = 'horario';

/** `horario` de la URL (`mensajes` por defecto: cuándo escribe el cliente, que es lo que se quiere saber). */
export function campoHorarioFrom(params: URLSearchParams): CampoHorario {
  return params.get(PARAM_HORARIO) === 'conversacionesIniciadas'
    ? 'conversacionesIniciadas'
    : 'mensajes';
}

/** El valor más alto de toda la matriz (el techo de la escala de color). */
export function maximoMatriz(matriz: CeldaHorario[][], campo: CampoHorario): number {
  return matriz.reduce(
    (maximo, fila) =>
      fila.reduce((maxFila, celda) => Math.max(maxFila, valorCelda(celda, campo)), maximo),
    0,
  );
}

/** Intensidad 0…1 de una celda, para elegir el tono. Con todo en cero devuelve 0. */
export function intensidadCelda(valor: number, maximo: number): number {
  if (maximo <= 0) return 0;
  return Math.min(1, Math.max(0, valor / maximo));
}

/** La celda con más actividad de la matriz (o `null` si no hubo nada en el periodo). */
export function celdaMasActiva(
  matriz: CeldaHorario[][],
  campo: CampoHorario,
): CeldaHorario | null {
  let mejor: CeldaHorario | null = null;

  for (const fila of matriz) {
    for (const celda of fila) {
      if (!mejor || valorCelda(celda, campo) > valorCelda(mejor, campo)) mejor = celda;
    }
  }

  return mejor && valorCelda(mejor, campo) > 0 ? mejor : null;
}

/**
 * «Miércoles a las 15:00» de una celda. La hora se pinta en formato 24 h y siempre con dos dígitos
 * porque es un eje, no una frase: «15:00» se lee mejor que «3 p. m.» en una lista de 24 columnas.
 */
export function etiquetaCelda(celda: CeldaHorario): string {
  const dia = DIAS_SEMANA_LARGOS[celda.dia] ?? `Día ${celda.dia}`;
  return `${dia} a las ${String(celda.hora).padStart(2, '0')}:00`;
}

/**
 * La frase del pico de actividad: «Miércoles a las 15:00, con 87 mensajes».
 * Sin actividad devuelve la explicación, no un «0 mensajes» que parece un error.
 */
export function describirPico(pico: CeldaHorario | null, campo: CampoHorario): string {
  if (!pico) return 'Sin actividad en el periodo.';

  const valor = valorCelda(pico, campo);
  const unidad = campo === 'mensajes' ? countLabel(valor, 'mensaje', 'mensajes') : `${formatCount(valor)} conversaciones`;

  return `${etiquetaCelda(pico)}, con ${unidad}`;
}

/** Horas con actividad (para dibujar solo las columnas que tienen datos en móvil). */
export function horasConActividad(matriz: CeldaHorario[][]): number[] {
  const horas = new Set<number>();

  for (const fila of matriz) {
    for (const celda of fila) {
      if (valorCelda(celda, 'mensajes') > 0 || valorCelda(celda, 'conversacionesIniciadas') > 0) {
        horas.add(celda.hora);
      }
    }
  }

  return [...horas].sort((a, b) => a - b);
}

// --- Piezas compartidas por el Panel y por Reportes (los dos leen el módulo 10) ---

/** Opciones del selector de intervalo del gráfico (los tres valores de `serie?intervalo=`). */
export const INTERVALOS_SERIE: { value: IntervaloReporte; label: string }[] = [
  { value: 'dia', label: 'Por día' },
  { value: 'semana', label: 'Por semana' },
  { value: 'mes', label: 'Por mes' },
];

/** Campo del mapa de calor con su rótulo (los dos conteos que trae cada celda). */
export const CAMPOS_HORARIO = [
  { value: 'mensajes', label: 'Cuándo escribe el cliente' },
  { value: 'conversacionesIniciadas', label: 'Cuándo entra el lead' },
] as const;

/** Etiquetas de los esqueletos mientras llega el primer resumen (mismo orden que las tarjetas). */
export const KPIS_EN_ESPERA = [
  'Conversaciones nuevas',
  'Mensajes',
  'Órdenes creadas',
  'Ventas confirmadas',
  'Ingresos',
];

/**
 * Frase del gráfico de la serie: promedio por punto y pico, con el formato de la métrica elegida.
 *
 * Vive acá (y no en una de las dos pantallas) porque el Panel y Reportes pintan el mismo gráfico con
 * el mismo encabezado: si cada uno lo escribiera, dos pantallas podrían describir el mismo dato con
 * dos redacciones distintas.
 */
export function descripcionSerie(
  resumen: ResumenSerie,
  metrica: MetricaSerieDef,
  intervalo: IntervaloReporte,
): string {
  const unidad = intervalo === 'dia' ? 'día' : intervalo === 'semana' ? 'semana' : 'mes';
  const pico = resumen.mayor
    ? `el pico fue el ${resumen.mayor.etiqueta}, con ${metrica.formatear(resumen.mayor.valor)}`
    : 'no hubo actividad en el periodo';

  return `Promedio de ${metrica.formatear(resumen.promedio)} por ${unidad} · ${pico}.`;
}

/** Dinero que puede faltar: `null` → «—» (nunca un 0 que parecería un dato). */
export function formatDineroOpcional(valor: number | null | undefined): string {
  return valor === null || valor === undefined ? '—' : formatNumber(valor);
}

// --- Los avisos de `serie` (el truncado del backend, que se muestra siempre) ---

/**
 * Lo que hay que decirle al admin sobre la serie que se está pintando.
 *
 * El backend nunca devuelve «una muestra silenciosa»: cuando supera los topes de filas
 * (`REPORTS_MAX_ROWS`) o de puntos lo dice en la respuesta. Acá se traduce a frases (y se explica
 * qué se está viendo de menos) porque un `truncado: true` en el JSON no le dice nada a nadie.
 */
export function avisosSerieReporte(serie: ReporteSerie): string[] {
  const avisos: string[] = [];

  if (serie.truncado) {
    avisos.push(
      'El backend alcanzó el tope de filas del periodo al armar la serie: los últimos puntos pueden estar incompletos.',
    );
  }

  if (serie.truncadoRango) {
    avisos.push(
      'El rango pedido tiene más puntos de los que se grafican: la serie muestra solo los primeros. Reduce el rango o cambia el intervalo.',
    );
  }

  if (serie.fueraDeRango > 0) {
    avisos.push(
      `${formatCount(serie.fueraDeRango)} filas cayeron fuera de la ventana pedida por el redondeo de zona horaria (es un dato de control del backend, no un error del reporte).`,
    );
  }

  return avisos;
}

// --- Tabla 1: resumen del periodo (canal, leads y órdenes) ---

/**
 * Una fila de conteo con etiqueta: el `id` es la clave del enum (para React) y `tone` el tono de la
 * píldora de estado (`@/theme/status`), el mismo que usan CRM y Órdenes.
 */
export interface FilaConteoReporte<T extends string> {
  id: T;
  label: string;
  tone: ToneName;
  total: number;
}

export interface FilaCanalReporte extends FilaConteoReporte<Channel> {
  /**
   * % sobre las conversaciones del periodo. Lo calcula la PANTALLA (el backend solo manda el conteo
   * por canal): `null` cuando no hubo conversaciones, porque «no se pudo calcular» no es 0.
   */
  porcentaje: string;
}

/** Conversaciones del periodo repartidas por canal (`resumen.conversaciones.porCanal`). */
export function filasCanalReporte(conversaciones: ResumenConversaciones): FilaCanalReporte[] {
  return conversaciones.porCanal
    .map((fila) => ({
      id: fila.channel,
      label: CHANNEL_LABEL[fila.channel] ?? fila.channel,
      tone: 'info' as ToneName,
      total: fila.total,
      porcentaje: formatPorcentaje(
        conversaciones.total > 0 ? (fila.total / conversaciones.total) * 100 : null,
      ),
    }))
    .sort((a, b) => b.total - a.total);
}

/** Los cuatro estados del lead (los mantiene el CRM), en el orden del kanban. */
export function filasEstadoLeadReporte(
  porEstadoLead: Record<EstadoLead, number>,
): FilaConteoReporte<EstadoLead>[] {
  return leadStatusOrder.map((estado) => ({
    id: estado,
    label: leadStatus[estado].label,
    tone: leadStatus[estado].tone,
    total: porEstadoLead[estado] ?? 0,
  }));
}

/** Los seis estados de la orden, en el orden del flujo y con `CANCELADA` al final. */
export function filasEstadoOrdenReporte(
  porEstado: Record<EstadoOrden, number>,
): FilaConteoReporte<EstadoOrden>[] {
  const estados: EstadoOrden[] = [...orderStatusFlow, 'CANCELADA'];

  return estados.map((estado) => ({
    id: estado,
    label: orderStatus[estado].label,
    tone: orderStatus[estado].tone,
    total: porEstado[estado] ?? 0,
  }));
}

// --- Tabla 2: la serie, punto por punto ---

/** Una fila de la tabla de la serie: el punto tal como se lee, sin recalcular nada. */
export interface FilaSerieReporte {
  /** Clave del punto del backend (`2026-09-10`, `2026-W38`, `2026-09`). */
  id: string;
  /** «10 sept 2026» / «Semana 38 de 2026» / «septiembre 2026», leído en la zona del negocio. */
  etiqueta: string;
  /** «10/09» / «S38» / «sep 26», la misma etiqueta del eje del gráfico. */
  etiquetaCorta: string;
  conversaciones: number;
  mensajes: number;
  ordenes: number;
  ventasConfirmadas: number;
  ingresos: number;
}

/**
 * Los puntos de la serie como filas de tabla, en el orden cronológico que manda el backend (no se
 * reordena: el «hueco en cero» del backend solo tiene sentido si la tabla va en el tiempo).
 */
export function filasSerieReporte(
  puntos: PuntoSerie[],
  intervalo: IntervaloReporte,
  zonaHoraria: string,
): FilaSerieReporte[] {
  return puntos.map((punto) => ({
    id: punto.periodo,
    etiqueta: etiquetaPuntoLarga(punto.periodo, intervalo, zonaHoraria),
    etiquetaCorta: etiquetaPunto(punto.periodo, intervalo),
    conversaciones: punto.conversaciones,
    mensajes: punto.mensajes,
    ordenes: punto.ordenes,
    ventasConfirmadas: punto.ventasConfirmadas,
    ingresos: punto.ingresos,
  }));
}

/** Suma de las columnas de la tabla de la serie (incluye cuántos puntos se sumaron). */
export interface TotalesSerieReporte {
  puntos: number;
  conversaciones: number;
  mensajes: number;
  ordenes: number;
  ventasConfirmadas: number;
  ingresos: number;
}

/**
 * Totales de la tabla de la serie: la suma de los puntos QUE SE ESTÁN VIENDO.
 *
 * No es «el total del periodo» —ese es el del resumen, que cuenta todo—: si el backend truncó la
 * serie, esta suma sale más baja a propósito y el pie de la tabla lo dice. Mezclar los dos números
 * sin decirlo sería la forma más fácil de que el reporte mienta.
 */
export function totalesSerieReporte(puntos: PuntoSerie[]): TotalesSerieReporte {
  return puntos.reduce<TotalesSerieReporte>(
    (totales, punto) => ({
      puntos: totales.puntos + 1,
      conversaciones: totales.conversaciones + punto.conversaciones,
      mensajes: totales.mensajes + punto.mensajes,
      ordenes: totales.ordenes + punto.ordenes,
      ventasConfirmadas: totales.ventasConfirmadas + punto.ventasConfirmadas,
      ingresos: totales.ingresos + punto.ingresos,
    }),
    { puntos: 0, conversaciones: 0, mensajes: 0, ordenes: 0, ventasConfirmadas: 0, ingresos: 0 },
  );
}

// --- Tabla 3: publicidad y CPA (una fila por anuncio o por campaña) ---

/** De dónde salió la fila (el `origen` del backend), en palabras. */
export const ORIGEN_CAMPANA_LABEL: Record<FilaCampanaReporte['origen'], string> = {
  catalogo: 'Catálogo de gasto',
  webhook: 'Solo webhook',
  sin_atribucion: 'Sin atribución',
};

/** Tono de la píldora de `origen`: la fila sin atribución se ve neutra, no como un anuncio. */
export const ORIGEN_CAMPANA_TONE: Record<FilaCampanaReporte['origen'], ToneName> = {
  catalogo: 'brand',
  webhook: 'warning',
  sin_atribucion: 'neutral',
};

/** Una fila de la tabla de publicidad: los conteos crudos y los tres textos que pueden ser «—». */
export interface FilaPublicidadReporte {
  id: string;
  /** Nombre cargado → id → «Sin atribución» (ver `etiquetaFilaCampana`). */
  label: string;
  /** «Anuncio 123 · Campaña 456», o la explicación de que llegó sin ids de Meta. */
  ids: string;
  origen: string;
  origenTone: ToneName;
  conversaciones: number;
  clientes: number;
  ordenes: number;
  ordenesConfirmadas: number;
  ingresos: number;
  gasto: number;
  /** Esta fila gasta lo del anuncio: su campaña tiene gasto cargado aparte (no se reparte). */
  gastoEnCampania: boolean;
  /** CPA listo para pintar: número, «sin ventas en el periodo» o «sin gasto cargado». */
  cpa: string;
  /** Tasa de conversión de la fila («—» si no tuvo conversaciones). */
  tasa: string;
  /** Ticket promedio de la fila («—» si no tuvo ventas). */
  ticket: string;
}

export function filasPublicidadReporte(items: FilaCampanaReporte[]): FilaPublicidadReporte[] {
  return items.map((fila) => ({
    id: fila.clave,
    label: etiquetaFilaCampana(fila),
    ids: idsFilaCampana(fila),
    origen: ORIGEN_CAMPANA_LABEL[fila.origen],
    origenTone: ORIGEN_CAMPANA_TONE[fila.origen],
    conversaciones: fila.conversaciones,
    clientes: fila.clientes,
    ordenes: fila.ordenes,
    ordenesConfirmadas: fila.ordenesConfirmadas,
    ingresos: fila.ingresos,
    gasto: fila.gasto,
    gastoEnCampania: fila.gastoEnCampania,
    cpa: cpaLabel(fila.cpa, fila.ordenesConfirmadas),
    tasa: formatPorcentaje(fila.tasaConversionPorcentaje),
    ticket: formatDineroOpcional(fila.ticketPromedio),
  }));
}

/** El pie de la tabla de publicidad: los totales del reporte con sus dos CPA bien separados. */
export interface TotalesPublicidadReporte {
  conversaciones: number;
  clientes: number;
  ordenes: number;
  ordenesConfirmadas: number;
  ingresos: number;
  gasto: number;
  gastoTotalRegistrado: number;
  cpa: string;
  cpaGlobal: string;
  tasa: string;
  ticket: string;
}

export function totalesPublicidadReporte(totales: TotalesCampanas): TotalesPublicidadReporte {
  return {
    conversaciones: totales.conversaciones,
    clientes: totales.clientes,
    ordenes: totales.ordenes,
    ordenesConfirmadas: totales.ordenesConfirmadas,
    ingresos: totales.ingresos,
    gasto: totales.gasto,
    gastoTotalRegistrado: totales.gastoTotalRegistrado,
    cpa: cpaLabel(totales.cpa, totales.ordenesConfirmadas),
    cpaGlobal: cpaLabel(totales.cpaGlobal, totales.ordenesConfirmadas),
    tasa: formatPorcentaje(totales.tasaConversionPorcentaje),
    ticket: formatDineroOpcional(totales.ticketPromedio),
  };
}

// --- Tabla 4: ranking de productos ---

/** Una fila del ranking de productos vendidos. */
export interface FilaProductoReporte {
  id: string;
  nombre: string;
  /** Unidades vendidas (suma de `cantidad` de las órdenes confirmadas). */
  cantidad: number;
  /** Órdenes DISTINTAS en las que aparece el producto. */
  ordenes: number;
  ingresos: number;
  /** Ticket promedio del producto («—» si no se puede calcular). */
  ticketPromedio: string;
}

/** Las filas del ranking, en el orden que decidió el backend (`ordenar=cantidad|ingresos`). */
export function filasProductosReporte(productos: ProductoVendido[]): FilaProductoReporte[] {
  return productos.map((producto, indice) => ({
    // El backend agrupa por nombre sin distinguir mayúsculas, pero nada garantiza que dos filas no lo
    // repitan: el índice asegura una clave única para React.
    id: `${indice}-${producto.nombre}`,
    nombre: producto.nombre,
    cantidad: producto.cantidad,
    ordenes: producto.ordenes,
    ingresos: producto.ingresos,
    ticketPromedio: formatDineroOpcional(producto.ticketPromedio),
  }));
}

/**
 * La frase del `truncado` del ranking de productos.
 *
 * El backend agrega en memoria sobre las primeras `REPORTS_MAX_ORDERS` órdenes del periodo: si se
 * superan, el ranking sigue siendo correcto en el orden pero deja de ser completo, y eso se dice.
 */
export function avisoProductosTruncado(productos: ReporteProductos): string | null {
  return productos.truncado
    ? `El periodo tiene más órdenes de las que se analizan de una vez (se analizaron ${formatCount(
        productos.ordenesAnalizadas,
      )}): el ranking es parcial.`
    : null;
}

// --- Tabla 5: por qué no cerró (motivos de no cierre) ---

/** Clave con la que el backend marca una conversación sin orden que el CRM todavía no clasificó. */
export const MOTIVO_SIN_CLASIFICAR = 'sin_clasificar';

/** El motivo del CRM, o la explicación de que esa conversación todavía no tiene uno. */
export function etiquetaMotivo(motivo: string): string {
  return motivo === MOTIVO_SIN_CLASIFICAR ? 'Sin clasificar en el CRM' : motivo;
}

/** Una fila de la tabla de motivos: el estado del lead + el motivo + cuántas conversaciones son. */
export interface FilaMotivoReporte {
  id: string;
  estadoLabel: string;
  tone: ToneName;
  motivo: string;
  total: number;
  /** % sobre las conversaciones sin orden (`—` si no hubo ninguna). Lo manda el backend. */
  porcentaje: string;
}

export function filasMotivosReporte(motivos: MotivoNoCierre[]): FilaMotivoReporte[] {
  return motivos.map((fila, indice) => ({
    id: `${indice}-${fila.estadoLead}-${fila.motivo}`,
    estadoLabel: leadStatus[fila.estadoLead]?.label ?? fila.estadoLead,
    tone: leadStatus[fila.estadoLead]?.tone ?? 'neutral',
    motivo: etiquetaMotivo(fila.motivo),
    total: fila.total,
    porcentaje: formatPorcentaje(fila.porcentaje),
  }));
}

export interface FilaEstadoSinOrdenReporte extends FilaConteoReporte<EstadoLead> {
  /** % sobre las conversaciones sin orden (la pantalla lo saca del conteo por estado). */
  porcentaje: string;
}

/**
 * Los mismos cuatro estados del lead, pero contando SOLO las conversaciones que no llegaron a una
 * orden: es la foto de «dónde se quedó» el embudo, y por eso se pinta aparte del resumen.
 */
export function filasEstadoSinOrdenReporte(
  porEstadoLead: Record<EstadoLead, number>,
  totalSinOrden: number,
): FilaEstadoSinOrdenReporte[] {
  return filasEstadoLeadReporte(porEstadoLead).map((fila) => ({
    ...fila,
    porcentaje: formatPorcentaje(totalSinOrden > 0 ? (fila.total / totalSinOrden) * 100 : null),
  }));
}

// --- Tabla 6: horarios de atención (las franjas con más actividad) ---

/** Una franja (día × hora) del mapa de calor, lista para la tabla del bloque de horarios. */
export interface FilaHoraReporte {
  id: string;
  /** «Miércoles a las 15:00», leído en la zona del negocio. */
  etiqueta: string;
  /** El día suelto («Miércoles»), para la columna. */
  dia: string;
  /** La hora suelta («15:00»), para la columna. */
  hora: string;
  valor: number;
  /** % sobre el total del propio mapa (`—` si no hubo actividad). */
  porcentaje: string;
}

/**
 * Las `limite` franjas (día × hora) con MÁS actividad del mapa, de mayor a menor.
 *
 * El mapa de calor enseña la forma de la semana, pero no deja leer el detalle; esta tabla es la que
 * responde «¿cuál es la hora pico?». El porcentaje se calcula sobre la suma del PROPIO mapa (no sobre
 * `totales`, que puede venir truncado), así los dos números de la fila siempre cuadran entre sí.
 */
export function horasMasActivas(
  matriz: CeldaHorario[][],
  campo: CampoHorario,
  limite = 6,
): FilaHoraReporte[] {
  const celdas = matriz.flat();
  const total = celdas.reduce((suma, celda) => suma + valorCelda(celda, campo), 0);

  return celdas
    .filter((celda) => valorCelda(celda, campo) > 0)
    .sort((a, b) => valorCelda(b, campo) - valorCelda(a, campo))
    .slice(0, limite)
    .map((celda) => {
      const valor = valorCelda(celda, campo);

      return {
        id: `${celda.dia}-${celda.hora}`,
        etiqueta: etiquetaCelda(celda),
        dia: DIAS_SEMANA_LARGOS[celda.dia] ?? `Día ${celda.dia}`,
        hora: `${String(celda.hora).padStart(2, '0')}:00`,
        valor,
        porcentaje: formatPorcentaje(total > 0 ? (valor / total) * 100 : null),
      };
    });
}

